import { get } from "lodash";
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Field } from "formik";
import { Form, Divider, Radio } from "semantic-ui-react";

import FormikSemanticInput from "../../forms/formik/FormikSemanticInput.jsx";
import FormikSemanticNumberInput from "../../forms/formik/FormikSemanticNumberInput.jsx";
import FormikSemanticCheckbox from "../../forms/formik/FormikSemanticCheckbox.jsx";

import { padNumber } from "../../../utils/numberUtils.js";
import { hydrateCopyObjectWithValue } from "../../../state/reducers/locale/copyUtils.js";
import { selectCopy } from "../../../state/reducers/locale/copySelector.js";

class ResourceBlockMultiples extends PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    formikProps: PropTypes.object.isRequired, // required for selectors to function
    apiErrors: PropTypes.object,
    maxAvailableInstances: PropTypes.number.isRequired,
    onShowOrHideMultiples: PropTypes.func.isRequired,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    id: "resource[0]",
    maxAvailableInstances: 1,
    copyKey: "project_wizard.resource_panel"
  };

  constructor(props) {
    super(props);

    const namingSchemeParam = `formikProps.values.${props.id}.namingScheme`;

    this.state = {
      showMultiples: get(props, namingSchemeParam, undefined) ? true : false
    };
  }

  // -- DATA MANAGEMENT -- //

  static newInstanceSchema() {
    return {
      namingScheme: "",
      number: 1,
      startAt: 1
    };
  }

  // -- HANDLERS -- //

  onShowMultiples = (event, data) => {
    const { id, formikProps, onShowOrHideMultiples } = this.props;

    let newValue = {
      ...get(formikProps, `values.${id}`, undefined)
    };

    if (data.checked) {
      newValue.namingScheme = "";
      newValue.number = 1;
      newValue.startAt = 1;
    } else {
      delete newValue.namingScheme;
      delete newValue.number;
      delete newValue.startAt;
    }

    onShowOrHideMultiples(newValue);

    if (data.checked) {
      const name = get(formikProps, `values.${id}.name`, "");
      formikProps.setFieldValue(
        `${id}.namingScheme`,
        `${name}${name ? " " : ""}{number}`
      );
    }

    this.setState({ showMultiples: data.checked });
  };

  generateExampleResourceName() {
    const { id, formikProps } = this.props;

    const useZeroPadding = get(
      formikProps,
      `values.${id}.useZeroPadding`,
      undefined
    );
    const namingScheme = get(
      formikProps,
      `values.${id}.namingScheme`,
      undefined
    );
    const startAt = get(formikProps, `values.${id}.startAt`, 1);

    const sequencer = useZeroPadding ? padNumber(startAt, 4) : startAt;

    return hydrateCopyObjectWithValue(namingScheme, sequencer);
  }

  // -- RENDER -- //

  render = () => {
    const { apiErrors, id, maxAvailableInstances, copyKey, copy } = this.props;
    const { showMultiples } = this.state;

    return (
      <Fragment>
        <Divider />
        <Form.Group>
          <Radio
            toggle
            data-testid="showMultiplesToggle"
            checked={showMultiples}
            label={copy(`${copyKey}.multiples_cta`)}
            onClick={this.onShowMultiples}
            style={{ marginLeft: "0.5em" }}
          />
        </Form.Group>

        {showMultiples && (
          <Fragment>
            <Form.Group widths="equal">
              <div className="equal width field">
                <Field
                  name={`${id}.number`}
                  label={copy(`${copyKey}.number_label`)}
                  placeholder={copy(`${copyKey}.number_placeholder`)}
                  type="number"
                  min={1}
                  max={maxAvailableInstances}
                  required={true}
                  autoFocus={false}
                  apiErrors={apiErrors}
                  component={FormikSemanticNumberInput}
                />
                <Field
                  name={`${id}.startAt`}
                  label={copy(`${copyKey}.start_at_label`)}
                  placeholder={copy(`${copyKey}.start_at_placeholder`)}
                  type="number"
                  min={0}
                  max={99999}
                  required={true}
                  autoFocus={false}
                  apiErrors={apiErrors}
                  component={FormikSemanticNumberInput}
                />
              </div>

              <div className="equal width field">
                <Field
                  name={`${id}.namingScheme`}
                  label={copy(`${copyKey}.naming_scheme_label`)}
                  placeholder={copy(`${copyKey}.naming_scheme_placeholder`)}
                  required={true}
                  autoFocus={true}
                  apiErrors={apiErrors}
                  component={FormikSemanticInput}
                />
                <p style={{ marginTop: "1em" }}>
                  {copy(`${copyKey}.naming_scheme_description`)}
                </p>
                <Field
                  name={`${id}.useZeroPadding`}
                  label={copy(`${copyKey}.use_zero_padding`)}
                  required={false}
                  apiErrors={apiErrors}
                  component={FormikSemanticCheckbox}
                />
                <label style={{ marginTop: "1em" }}>
                  {copy(`${copyKey}.example_resource_name_label`)}
                </label>
                <p>{this.generateExampleResourceName()}</p>
              </div>
            </Form.Group>
          </Fragment>
        )}
      </Fragment>
    );
  };
}

const mapStateToProps = state => ({
  copy: selectCopy(state)
});

export default connect(mapStateToProps)(ResourceBlockMultiples);
